<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useTranslate } from '@/shared/lib'
import { VIcons } from '@/shared/ui'
import type { RelatedObjectCard } from '../model/usePassportRuntimeRelated'

const props = defineProps<{
  cards: RelatedObjectCard[]
  loading?: boolean
}>()

const route = useRoute()
const router = useRouter()
const { trs } = useTranslate()

const hasCards = computed(() => props.cards.length > 0)

function navigateTo(objectId: number) {
  const loc = router.resolve({
    name: route.name ?? 'object.inventory.elements.edit',
    params: { ...route.params, invent_object_id: String(objectId) },
    query: route.query,
  })
  router.push(loc)
}
</script>

<template>
  <div v-if="loading" class="prt-related-cards__state">
    <VSkeletonLoader type="card" />
  </div>
  <div v-else-if="hasCards" class="prt-related-cards">
    <div
      v-for="card in cards"
      :key="card.fieldId"
      class="prt-related-cards__card"
      tabindex="0"
      role="link"
      @click="navigateTo(card.objectId)"
      @keydown.enter.prevent="navigateTo(card.objectId)"
    >
      <div class="prt-related-cards__field-name">{{ card.fieldName }}</div>
      <div class="prt-related-cards__object-name">{{ card.name }}</div>
      <div class="prt-related-cards__meta">
        <span class="prt-related-cards__type">{{ card.typeName }}</span>
        <VChip
          v-if="card.status"
          size="x-small"
          variant="tonal"
          class="prt-related-cards__status"
        >
          {{ card.status }}
        </VChip>
      </div>
    </div>
  </div>
</template>

<style scoped>
.prt-related-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
}

.prt-related-cards__card {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  padding: 12px 14px;
  background: #fff;
  cursor: pointer;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
}

.prt-related-cards__card:hover {
  border-color: rgba(25, 118, 210, 0.45);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.prt-related-cards__field-name {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.5);
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.prt-related-cards__object-name {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
  margin-bottom: 8px;
}

.prt-related-cards__meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.prt-related-cards__type {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
}

.prt-related-cards__status {
  font-size: 11px;
}

.prt-related-cards__state {
  padding: 8px 0;
}
</style>
